import { useState } from "react";
// import { useForm } from 'react-hook-form';
import { Edit3, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAppDispatch, useAppSelector } from "@/hooks/storeHooks";
import { useForm } from "react-hook-form";
import { Organization, User } from "@/types/apiTypes";
import { Textarea } from "@/components/ui/textarea";

import { Field } from "@/dev/ui/Field";
import { Input } from "@/components/ui/input";
import { flatten, unflatten } from "flat";
import { useUpdateUserMutation } from "@/store/apis/userApi";
import { updateUser as UpUser } from "@/store/reducers/userSlice";
import { setNotification } from "@/store/reducers/notificationSlice";

const Me = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [updateUser] = useUpdateUserMutation();
  const { user } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    reset,

    formState: { errors, dirtyFields },
  } = useForm<User & Organization>({
    defaultValues: user || {},
  });

  const getTheDirty = <T extends typeof dirtyFields>({
    data,
    dirtyFields,
  }: {
    data: User & Organization;
    dirtyFields: T;
  }): Partial<User & Organization> => {
    const flatData: Record<string, string> = flatten(data) || {};
    const flatDirtyFields: Record<string, string> = flatten(dirtyFields) || {};
    const dirtyData: any = {};

    Object.keys(flatDirtyFields).forEach((key) => {
      if (flatDirtyFields[key]) {
        dirtyData[key] = flatData[key];
      }
    });
    return unflatten(dirtyData);
  };

  const onSubmit = (data: User & Organization) => {
    const dirtyData = getTheDirty({ data, dirtyFields });
    if (Object.keys(dirtyData).length === 0) return;
    const { surname, given_name, phone_number } = dirtyData;

    const user = { surname, given_name, phone_number };
    updateUser(user)
      .unwrap()
      .then((res) => {
        if (res.data) {
          dispatch(setNotification({ message: "User updated successfully", type: "success" }));
          dispatch(UpUser(res.data));
        } else {
          dispatch(setNotification({ message: "Failed to update user", type: "error" }));
        }
      })
      .catch(() => {
        dispatch(setNotification({ message: "Failed to update user", type: "error" }));
      });

    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">פרופיל אישי</h2>

          {!isEditing ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2">
              <Edit3 className="w-4 h-4" />
              עריכה
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                type="submit"
                //   onClick={handleSubmit(onSubmit)}
                className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                שמירה
              </Button>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => {
                  reset();
                  setIsEditing(false);
                }}
                className="flex items-center gap-2">
                <X className="w-4 h-4" />
                ביטול
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="שם משפחה" error={errors.surname?.message} required>
            <Input
              placeholder="לוי"
              {...register("surname", { required: "שם משפחה הוא שדה חובה" })}
              readOnly={!isEditing}
            />
          </Field>

          <Field label="שם פרטי" error={errors["given_name"]?.message} required>
            <Input
              placeholder="דנה"
              {...register("given_name", { required: "שם פרטי הוא שדה חובה" })}
              readOnly={!isEditing}
            />
          </Field>
        </div>

        <Field label="כתובת אימייל">
          <Input
            type="email"
            placeholder="name@domain.com"
            value={user?.email ?? ""}
            readOnly
            disabled
            className="bg-muted/50 cursor-not-allowed"
          />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="מספר טלפון" error={errors.phone_number?.message} required>
            <Input
              placeholder="050-1234567"
              dir="rtl"
              type="tel"
              {...register("phone_number", {
                required: "מספר טלפון הוא שדה חובה",
                pattern: {
                  value: /^[\d\s()+-]{7,20}$/,
                  message: "מספר טלפון לא תקין",
                },
              })}
              readOnly={!isEditing}
            />
          </Field>

          <Field label="מיקום" error={errors.address?.message}>
            <Input disabled placeholder="עיר/יישוב (כל ערך)" {...register("address")} readOnly={!isEditing} />
          </Field>
        </div>

        <Field label="שם הארגון">
          <Input disabled placeholder="שם הארגון (אופציונלי)" {...register("org_name")} readOnly={!isEditing} />
        </Field>

        <Field label="מידע על הארגון">
          <Textarea
            disabled
            rows={4}
            placeholder="תיאור חופשי (אופציונלי)"
            {...register("org_info")}
            readOnly={!isEditing}
          />
        </Field>
      </form>
    </div>
  );
};
export default Me;
